import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';
import { mod } from 'react-swipeable-views-core';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography
} from '@material-ui/core';
import { ImgWithRetry } from 'components/layout';
import ThumbnailNavControls from 'components/layout/ImageCarousel/ThumbnailNavControls';
import _ from "lodash";

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

const thumbnailWidth = 150;
const thumbnailHorizontalMargin = 4;
const maxThumbnailCarouselCount = 11; // Must be odd number

const ThumbnailNav = ({ classes, mappedImages, imageIndex, setImageIndex, selectedSourceIndex, setSelectedSourceIndex }) => {
  const [windowWidth, setWindowWidth] = useState(null);
  const [thumbnailCarouselCount, setThumbnailCarouselCount] = useState(getThumbnailCarouselCount());
  const [thumbnailIndex, setThumbnailIndex] = useState((thumbnailCarouselCount - 1) / 2);
  const visibleDistanceFromCenter = (thumbnailCarouselCount - 1) / 2 ;
  const virtualIndex = mod(imageIndex, mappedImages.length);

  const onWindowResizeDebounced = _.debounce(onWindowResize, 500);
  useEffect(() => {
    window.addEventListener("resize", onWindowResizeDebounced);
    onWindowResize();
    return () => {
      window.removeEventListener("resize", onWindowResizeDebounced);
    }
  }, []);

  const isFirst = useRef(true);
  useEffect(() => {
    if (!mappedImages.length) return;
    const virtualIndex = mod(imageIndex, mappedImages.length);
    if (isFirst.current) {
      isFirst.current = false; 
      const clampedIndex = getClampedThumbnailIndex(virtualIndex, visibleDistanceFromCenter);
      setThumbnailIndex(clampedIndex);
    } else {
      if (virtualIndex > thumbnailIndex + visibleDistanceFromCenter) {
        setThumbnailIndex(virtualIndex - visibleDistanceFromCenter);
      } else if (virtualIndex < thumbnailIndex - visibleDistanceFromCenter) {
        setThumbnailIndex(virtualIndex + visibleDistanceFromCenter);
      }
    }
  }, [imageIndex])
  
  useEffect(() => {
    if (selectedSourceIndex == null) return;
    const thumbnailIndex = selectedSourceIndex + visibleDistanceFromCenter;
    const clampedIndex = getClampedThumbnailIndex(thumbnailIndex, visibleDistanceFromCenter); 
    setThumbnailIndex(clampedIndex);
    setImageIndex(selectedSourceIndex);
  }, [selectedSourceIndex])

  useEffect(() => {
    if (!windowWidth) return;
    const newSlideCount = getThumbnailCarouselCount();
    setThumbnailCarouselCount(newSlideCount);
    const visibleDistanceFromCenter = (newSlideCount - 1) / 2
    const clampedIndex = getClampedThumbnailIndex(thumbnailIndex, visibleDistanceFromCenter)
    setThumbnailIndex(clampedIndex);
  }, [windowWidth]);

  const handleOnTransitionEnd = () => {
    if (selectedSourceIndex != null) {
      setTimeout(() => setSelectedSourceIndex(null), 50);
    }
  }

  function getClampedThumbnailIndex(unclampedIndex, visibleDistanceFromCenter) {
    return Math.min(Math.max(unclampedIndex, visibleDistanceFromCenter), mappedImages.length - 1 - visibleDistanceFromCenter);
  }

  function getThumbnailCarouselCount() {
    const totalSlideWidth = window.innerWidth - 64 - 16 - 80; // Subtract modal margin, thumbnail nav button padding and width
    const singleSlideWidth = thumbnailWidth + 2 * thumbnailHorizontalMargin;
    const slideCount = Math.floor(totalSlideWidth / singleSlideWidth);
    const roundedSlideCount = slideCount % 2 === 0 ? slideCount - 1 : slideCount;
    const cappedSlideCount = roundedSlideCount > maxThumbnailCarouselCount ? maxThumbnailCarouselCount : roundedSlideCount;
    return cappedSlideCount;
  }

  function onWindowResize() {
    setWindowWidth(window.innerWidth);
  }

  const ThumbnailSlideRenderer = ({ index, key, virtualize = true }) => {
    const virtualThumbnailIndex = mod(index, mappedImages.length);
    const isSelected = virtualIndex === index;
    const { source, isFirstOfSource, isLastOfSource, thumbnailUrl } = mappedImages[virtualThumbnailIndex];
    const isFirstInTray = virtualize ? virtualThumbnailIndex === thumbnailIndex - visibleDistanceFromCenter : virtualThumbnailIndex === 0;
    const isLastInTray = virtualize ? virtualThumbnailIndex === thumbnailIndex + visibleDistanceFromCenter : virtualThumbnailIndex === mappedImages.length - 1;
    return (
      <div key={key} id={`propthumbnail${index}`}>
        <div className={classes.sourceInfoContainer}>
          {isFirstOfSource && <Typography variant='subtitle2' className={classes.sourceText}>{source}</Typography>}
          <div className={classes.sourceLine} style={isFirstInTray ? { marginLeft: 8 } : isLastOfSource || isLastInTray ? { marginRight: 8 } : {}}></div>
        </div>
        <div className={classes.thumbnailContainer}>
          {selectedSourceIndex == null &&
            <ImgWithRetry alt={`propertyphotothumbnail ${index}`} src={thumbnailUrl} className={classNames(classes.thumbnailImage, isSelected && classes.selectedThumbnail)} onClick={() => setImageIndex(index)} retryCount={2}/>
          }
        </div>
      </div>
      
    )
  }

  return (
    mappedImages.length > 1 &&
      <div className={classes.thumbnailNavScrollContainer}>
        {mappedImages.length > thumbnailCarouselCount &&
          <ThumbnailNavControls
            mappedImagesLength={mappedImages.length}
            visibleDistanceFromCenter={visibleDistanceFromCenter}
            thumbnailIndex={thumbnailIndex}
            setThumbnailIndex={setThumbnailIndex}
          />
        }
        <div className={classes.thumbnailNavContainer} style={{ width: (150 + 2 * thumbnailHorizontalMargin) * thumbnailCarouselCount}}>
          {mappedImages.length <= thumbnailCarouselCount
            ? mappedImages.map((img, index) => {
                return (
                  <div style={{ width: thumbnailWidth + 2 * thumbnailHorizontalMargin }}>
                    <ThumbnailSlideRenderer key={index} index={index} virtualize={false} />
                  </div>
                )
              })
            : <VirtualizeSwipeableViews
                overscanSlideBefore={visibleDistanceFromCenter + 1}
                overscanSlideAfter={visibleDistanceFromCenter + 1}
                index={thumbnailIndex}
                slideRenderer={ThumbnailSlideRenderer}
                slideClassName={classes.thumbnailSlide}
                animateTransitions={true}
                style={{ width: thumbnailWidth + 2 * thumbnailHorizontalMargin, overflowX: 'visible' }}
                onTransitionEnd={handleOnTransitionEnd}
              />
          }
        </div>
      </div>
  );
}

const styles = theme => ({
  thumbnailNavScrollContainer: {
    zIndex: 1,
    backgroundColor: 'rgba(25, 25, 25)'
  },
  thumbnailNavContainer: {
    display: 'flex',
    padding: '0 4px',
    overflow: 'hidden',
    margin: 'auto',
    justifyContent: 'center'
  },
  thumbnailContainer: {
    margin: `8px ${thumbnailHorizontalMargin}px`,
    height: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  thumbnailImage: {
    height: '100%',
    width: '100%',
    borderRadius: 4,
    cursor: 'pointer',
    opacity: 0.6,
    '&:hover': {
      opacity: 1
    },
    objectFit: 'cover'
  },
  selectedThumbnail: {
    boxShadow: '0 0 0 4px #004990',
    opacity: 1
  },
  thumbnailSlide: {
    overflow: 'visible!important'
  },
  sourceInfoContainer: {
    height: 24,
    marginBottom: -4,
    position: 'relative',
    paddingTop: 1,
    display: 'flex',
    alignItems: 'center'
  },
  sourceText: {
    color: 'white',
    lineHeight: '24px',
    backgroundColor: 'rgb(25, 25, 25)',
    position: 'absolute',
    zIndex: 1,
    maxWidth: 162,
    padding: '0 4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  sourceLine: {
    backgroundColor: 'white',
    height: 2,
    flexGrow: 1
  }
});

export default withStyles(styles)(ThumbnailNav);